import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, TextField } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectDefaultAssessmentScaleGroupId } from 'store/options';
import {
  useErrorClearing,
  useUniqueNameValidation,
} from 'hooks/GoalsAndSubgoalsForm';
import { formTexts } from 'i18n';
import { GoalField } from 'components/GoalsAndSubgoalsForm/interfaces';
import GoalSettingsDialog from 'pages/Scorecard/EditScorecard/GoalSettingsDialog.component';
import GoalTypeSelect from 'pages/Scorecard/EditScorecard/GoalTypeSelect.component';
import useSubgoalListActions from 'pages/Scorecard/EditScorecard/useSubgoalListActions';
import restrictToVerticalAxis from 'pages/Scorecard/EditScorecard/verticalDnd';
import {
  AddSubgoalRow,
  ChevronButton,
  ContentArea,
  DescriptionFieldGroup,
  FieldGroup,
  FieldLabel,
  FieldsGrid,
  Header,
  HeaderInner,
  HeaderTitle,
  RemoveGoalButton,
  SectionRoot,
  SettingsLink,
  SubgoalsBranch,
  SubgoalsBranchInner,
  SubgoalsHeader,
  SubgoalsList,
  descriptionFieldSx,
} from 'pages/Scorecard/EditScorecard/GoalEditor.styled';
import {
  DragHandleButton,
  DragHandleColumn,
} from 'pages/Scorecard/EditScorecard/SubgoalEditor.styled';
import CommonGoalSubgoalEditor from './CommonGoalSubgoalEditor.component';
import useCommonGoalEditGating from './useCommonGoalEditGating';

interface CommonGoalRegularEditorProps {
  goalIndex: number;
  goalsCount: number;
  onRemove?: () => void;
}

const newSubgoalDefaults = (assessmentScaleGroupId: string | number) => ({
  name: '',
  description: '',
  weight: 0,
  assessmentScaleGroupId,
});

interface SortableSubgoalProps {
  id: string;
  goalIndex: number;
  subgoalIndex: number;
  onRemove: () => void;
  canDelete: boolean;
}

function SortableSubgoal({
  id,
  goalIndex,
  subgoalIndex,
  onRemove,
  canDelete,
}: SortableSubgoalProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <CommonGoalSubgoalEditor
      goalIndex={goalIndex}
      subgoalIndex={subgoalIndex}
      onRemove={onRemove}
      canDelete={canDelete}
      containerRef={setNodeRef}
      containerStyle={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
      }}
      dragHandle={
        <DragHandleColumn>
          <DragHandleButton
            size="small"
            aria-label="reorder subgoal"
            {...attributes}
            {...listeners}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </DragHandleButton>
        </DragHandleColumn>
      }
    />
  );
}

export default function CommonGoalRegularEditor({
  goalIndex,
  goalsCount,
  onRemove,
}: CommonGoalRegularEditorProps) {
  const { control, watch } = useFormContext<GoalField>();
  const { formatMessage } = useIntl();
  const goal = watch(`goals.${goalIndex}`);
  const [expanded, setExpanded] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const nameValidationRules = useUniqueNameValidation('goals', goalIndex);
  useErrorClearing(goalIndex);
  const { canEdit, canDeleteGoal } = useCommonGoalEditGating(
    goalIndex,
    goalsCount,
  );
  const defaultAssessmentScaleGroupId = useSelector(
    selectDefaultAssessmentScaleGroupId,
  );
  const {
    fields,
    sensors,
    handleAppendSubgoal,
    handleRemoveSubgoal,
    handleReorderSubgoals,
  } = useSubgoalListActions(goalIndex, () =>
    newSubgoalDefaults(defaultAssessmentScaleGroupId),
  );

  const goalNamePlaceholder = formatMessage({
    id: 'pages.scorecards.edit.goalNamePlaceholder',
    defaultMessage: 'Goal name',
  });
  const descriptionPlaceholder = formatMessage({
    id: 'pages.scorecards.edit.descriptionPlaceholder',
    defaultMessage: 'Add a description for this goal',
  });

  return (
    <SectionRoot>
      <Header>
        <HeaderInner>
          <ChevronButton
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'collapse goal' : 'expand goal'}
          >
            {expanded ? (
              <ExpandLess sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 16 }} />
            )}
          </ChevronButton>
          <HeaderTitle>{goal?.name || `Goal ${goalIndex + 1}`}</HeaderTitle>
          <SettingsLink type="button" onClick={() => setSettingsOpen(true)}>
            <SettingsIcon sx={{ fontSize: 20 }} />
            <FormattedMessage
              id="pages.scorecards.edit.goalSettings"
              defaultMessage="Goal settings"
            />
          </SettingsLink>
          {onRemove && canDeleteGoal && (
            <RemoveGoalButton
              onClick={onRemove}
              size="small"
              aria-label="remove goal"
            >
              <DeleteIcon fontSize="small" />
            </RemoveGoalButton>
          )}
        </HeaderInner>
      </Header>
      {expanded && (
        <ContentArea>
          <FieldsGrid>
            <FieldGroup className="name">
              <FieldLabel htmlFor={`cg-goal-${goalIndex}-name`}>
                <FormattedMessage
                  id="pages.scorecards.edit.goalName"
                  defaultMessage="Goal Name"
                />
                <span className="required">*</span>
              </FieldLabel>
              <Controller
                name={`goals.${goalIndex}.name`}
                control={control}
                rules={{
                  required: formatMessage(formTexts.errorRequired),
                  maxLength: {
                    value: 256,
                    message: formatMessage(formTexts.errorMaxLength, {
                      fieldName: '',
                      maxLength: 256,
                    }),
                  },
                  validate: nameValidationRules,
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    id={`cg-goal-${goalIndex}-name`}
                    fullWidth
                    size="small"
                    placeholder={goalNamePlaceholder}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 256 }}
                    disabled={!canEdit}
                  />
                )}
              />
            </FieldGroup>
            <GoalTypeSelect
              goalIndex={goalIndex}
              disabled={!canEdit}
              className="type"
            />
          </FieldsGrid>
          <DescriptionFieldGroup>
            <FieldLabel htmlFor={`cg-goal-${goalIndex}-description`}>
              <FormattedMessage
                id="pages.scorecards.edit.description"
                defaultMessage="Description"
              />
            </FieldLabel>
            <Controller
              name={`goals.${goalIndex}.description`}
              control={control}
              rules={{
                maxLength: {
                  value: 3000,
                  message: formatMessage(formTexts.errorMaxLength, {
                    fieldName: '',
                    maxLength: 3000,
                  }),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  id={`cg-goal-${goalIndex}-description`}
                  fullWidth
                  size="small"
                  multiline
                  placeholder={descriptionPlaceholder}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  inputProps={{ maxLength: 3000 }}
                  disabled={!canEdit}
                  sx={descriptionFieldSx}
                />
              )}
            />
          </DescriptionFieldGroup>
          <SubgoalsBranch>
            <SubgoalsBranchInner>
              <SubgoalsHeader>
                <FormattedMessage
                  id="pages.scorecards.edit.subgoals"
                  defaultMessage="Subgoals"
                />
              </SubgoalsHeader>
              <SubgoalsList>
                {canEdit ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleReorderSubgoals}
                  >
                    <SortableContext
                      items={fields.map((f) => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {fields.map((field, subgoalIndex) => (
                        <SortableSubgoal
                          key={field.id}
                          id={field.id}
                          goalIndex={goalIndex}
                          subgoalIndex={subgoalIndex}
                          onRemove={() => handleRemoveSubgoal(subgoalIndex)}
                          canDelete={fields.length > 1}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  fields.map((field, subgoalIndex) => (
                    <CommonGoalSubgoalEditor
                      key={field.id}
                      goalIndex={goalIndex}
                      subgoalIndex={subgoalIndex}
                      onRemove={() => handleRemoveSubgoal(subgoalIndex)}
                      canDelete={fields.length > 1}
                    />
                  ))
                )}
                <AddSubgoalRow>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAppendSubgoal}
                    sx={{
                      borderColor: '#2085FF',
                      color: '#2085FF',
                      fontSize: 14,
                      fontWeight: 700,
                      lineHeight: '22px',
                      height: 32,
                      padding: '6px 16px',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#176ACE',
                        color: '#176ACE',
                        background: 'rgba(32,133,255,0.06)',
                      },
                    }}
                  >
                    <FormattedMessage
                      id="pages.scorecards.edit.addSubgoal"
                      defaultMessage="Add subgoal"
                    />
                  </Button>
                </AddSubgoalRow>
              </SubgoalsList>
            </SubgoalsBranchInner>
          </SubgoalsBranch>
        </ContentArea>
      )}
      {settingsOpen && (
        <GoalSettingsDialog
          goalIndex={goalIndex}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </SectionRoot>
  );
}
